import { Clock } from 'lucide-react'
import { type FC, useEffect, useState } from 'react'

import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { useAuditHistory } from 'hooks/useAuditHistory'
import { Badge, Button } from 'slab'
import { type AuditHistoryParams, type BrokersAudit } from 'types/audit'
import { getLocaleDateString } from 'utils'

interface AuditHistoryDrawerProps {
  params: AuditHistoryParams | null
  isOpen: boolean
  title?: string
  containerClasses?: string
}

const AuditHistoryDrawer: FC<AuditHistoryDrawerProps> = ({
  params,
  isOpen,
  title: _title = 'Audit History',
  containerClasses = '',
}) => {
  const [currentPage, setCurrentPage] = useState(1)

  // Create params with current page
  const paginatedParams = params ? { ...params, page: currentPage } : null
  const { data, isLoading, error } = useAuditHistory(isOpen ? paginatedParams : null)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Reset page when params change
  useEffect(() => {
    setCurrentPage(1)
  }, [params?.auditable_type, params?.auditable_id])

  return (
    <div className={containerClasses}>
      <AnimateHeightAndOpacity isOpen={isOpen}>
        <div className='pt-2'>
          {isLoading && (
            <div className='flex justify-center py-4'>
              <LoadingSpinnerCentered />
            </div>
          )}

          {error && (
            <div className='text-red-600 text-sm bg-red-50 p-3 rounded border'>
              Error loading audit history: {error}
            </div>
          )}

          {data && data.data.length === 0 && (
            <div className='text-gray-500 text-sm text-center py-4'>
              No audit history found
            </div>
          )}

          {data && data.data.length > 0 && (
            <div className='space-y-2'>
              {data.data.map((audit) => (
                <AuditItem key={audit.id} audit={audit} />
              ))}

              {data.pagination.total_pages > 1 && (
                <div className='flex justify-between items-center pt-3 border-t border-gray-200'>
                  <div className='text-sm text-gray-500'>
                    Page {data.pagination.current_page} of {data.pagination.total_pages}
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      type='button'
                      variant='tertiary'
                      size='small'
                      disabled={!data.pagination.prev_page}
                      onClick={() => data.pagination.prev_page && handlePageChange(data.pagination.prev_page)}
                    >
                      Previous
                    </Button>
                    <Button
                      type='button'
                      variant='tertiary'
                      size='small'
                      disabled={!data.pagination.next_page}
                      onClick={() => data.pagination.next_page && handlePageChange(data.pagination.next_page)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}

const AuditItem: FC<{ audit: BrokersAudit }> = ({ audit }) => {
  const formatAction = (action: string) => {
    switch (action) {
      case 'create':
        return 'Created'
      case 'update':
        return 'Updated'
      case 'destroy':
        return 'Deleted'
      default:
        return action.charAt(0).toUpperCase() + action.slice(1)
    }
  }

  const getBadgeType = (action: string): 'success' | 'info' | 'critical' | 'neutral' => {
    switch (action) {
      case 'create':
        return 'success'
      case 'update':
        return 'info'
      case 'destroy':
        return 'critical'
      default:
        return 'neutral'
    }
  }

  const hasChanges = audit.audited_changes && Object.keys(audit.audited_changes).length > 0

  return (
    <div className='border rounded bg-white p-3 text-sm'>
      <div className='flex items-center justify-between gap-4'>
        {/* Left side - Action and changes */}
        <div className='flex-1 min-w-0'>
          <div className='flex items-center gap-2 mb-1'>
            <Badge type={getBadgeType(audit.action)}>
              {formatAction(audit.action)}
            </Badge>
            <div className='flex items-center text-xs text-gray-500'>
              <Clock size={12} className='mr-1' />
              {getLocaleDateString(new Date(audit.created_at))}
            </div>
          </div>

          {/* Inline changes */}
          {hasChanges && (
            <div className='space-y-1'>
              {Object.entries(audit.audited_changes).map(([field, changes]) => (
                <InlineAuditChange
                  key={field}
                  field={field}
                  changes={changes}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right side - User info */}
        <div className='flex-shrink-0 text-right'>
          {audit.user
            ? (
              <div className='text-xs text-gray-600'>
                <div className='font-medium'>{audit.user.email}</div>
                <div className='text-gray-500 capitalize'>
                  {audit.user.role_name}
                </div>
              </div>
              )
            : (
              <div className='text-xs text-gray-400'>
                System
              </div>
              )}
        </div>
      </div>
    </div>
  )
}

const InlineAuditChange: FC<{ field: string; changes: unknown }> = ({ field, changes }) => {
  const formatFieldName = (field: string) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return <span className='text-gray-400 italic'>empty</span>
    }
    if (typeof value === 'boolean') {
      return <span className={value ? 'text-green-600' : 'text-red-600'}>{String(value)}</span>
    }
    if (typeof value === 'object') {
      return <code className='text-xs bg-gray-100 px-1 rounded'>{JSON.stringify(value)}</code>
    }
    return String(value)
  }

  // Changes can be an array [oldValue, newValue] or just the new value
  const isArray = Array.isArray(changes)
  const oldValue = isArray ? changes[0] : null
  const newValue = isArray ? changes[1] : changes

  return (
    <div className='text-xs text-gray-600'>
      <span className='font-medium text-gray-700'>{formatFieldName(field)}:</span>
      {isArray
        ? (
          <span className='ml-1'>
            <span className='bg-red-50 px-1 py-0.5 rounded text-red-700'>
              {formatValue(oldValue)}
            </span>
            <span className='mx-1 text-gray-400'>→</span>
            <span className='bg-green-50 px-1 py-0.5 rounded text-green-700'>
              {formatValue(newValue)}
            </span>
          </span>
          )
        : (
          <span className='ml-1 bg-green-50 px-1 py-0.5 rounded text-green-700'>
            {formatValue(newValue)}
          </span>
          )}
    </div>
  )
}

export default AuditHistoryDrawer
